import axios from "axios";
import constants from "../../Constants/constants";
import { COOKIE, getCookie, deleteCookie } from "../Cookie";
import { encodeURI } from "js-base64";

// The access token lives in module scope only -- never a cookie or localStorage.
// It is lost on page refresh by design and recovered from the HttpOnly refresh cookie.

let accessToken = null;

export const setAccessToken = (token) => { accessToken = token || null; };
export const getAccessToken = () => accessToken;

export const cookiesToDelete = () => {
	Object.values(COOKIE).forEach((name) => deleteCookie(name, "/"));
	sessionStorage.clear();
	localStorage.clear();
}

// withCredentials lets the browser attach the HttpOnly refresh and device cookies.
const authClient = axios.create({ withCredentials: true });

// No interceptors: a failing refresh must never re-enter refresh and loop.
const rawClient = axios.create({ withCredentials: true });

authClient.interceptors.request.use((config) => {
	// Capture the body before axios serialises it, or a retry double-encodes it.
	if (!("__rawData" in config)) config.__rawData = config.data;
	if (accessToken) config.headers.Authorization = `Bearer ${accessToken}`;
	return config;
});

export { authClient };

// Endpoints reachable without a session. A 401 from these must never redirect,
// or the login page can bounce in a loop.
const AUTH_FREE_PATHS = [
	"/login",
	"/check-username",
	"/forgot-password",
	"/validate-token",
	"/reset-password",
	"/auth/refresh",
];

const isAuthFree = (param) => AUTH_FREE_PATHS.some((path) => param?.startsWith(path));

// Only these mean the credential can be renewed; every other auth failure is terminal.
const REFRESHABLE_CODES = ["AUTH_TOKEN_EXPIRED", "AUTH_TOKEN_MISSING"];

// Legacy fallback: the API predates error_code and reported expiry as prose.
const LEGACY_SESSION_ENDED = ["invalid token", "token required", "token expired"];

const isSessionEnded = (data) => {
	const message = data?.error_message || data?.message;
	if (!message) return false;
	return LEGACY_SESSION_ENDED.includes(String(message).toLowerCase());
};

let loggingOut = false;

// Guarded so several concurrent 401s cause one redirect, not a storm.
export const forceLogout = () => {
	if (loggingOut) return;
	loggingOut = true;
	setAccessToken(null);
	// Read before clearing -- cookiesToDelete() wipes cookies and localStorage.
	const role = getCookie(COOKIE.Role);
	const surveyUrl = localStorage.getItem("surveyUrl");
	cookiesToDelete();
	let route = role === "0" ? "/student-login" : "/staff-login";
	if (surveyUrl) route += `?${encodeURI(surveyUrl)}`;
	window.location.href = constants.BaseUrl + route;
}

let refreshPromise = null;
let refreshDeadUntil = 0;

// Single-flight: concurrent callers share one request, or each would rotate the
// refresh token separately and the server would read that as token reuse.
export const doRefresh = () => {
	if (Date.now() < refreshDeadUntil) {
		return Promise.reject(new Error("refresh temporarily unavailable"));
	}
	if (!refreshPromise) {
		refreshPromise = rawClient({
			method: "post",
			url: `${constants.ApiUrl}/auth/refresh`,
		})
			.then((res) => {
				const token = res?.data?.token;
				if (!token) throw new Error("refresh returned no token");
				setAccessToken(token);
				return token;
			})
			.catch((err) => {
				// Brief circuit-break so a dead session doesn't retry-storm.
				refreshDeadUntil = Date.now() + 30000;
				throw err;
			})
			.finally(() => { refreshPromise = null; });
	}
	return refreshPromise;
};

// Recovers the session on app start. A failure is the ordinary logged-out case.
export const bootstrapAuth = () => {
	// No prior session marker: skip the network call entirely.
	if (!getCookie(COOKIE.UserId)) return Promise.resolve(false);
	return doRefresh().then(() => true).catch(() => false);
};

// status is null when no HTTP response arrived. Blob error bodies are parsed back
// to JSON, or an auth failure on a download would be invisible.
const normalizeError = async (err) => {
	const response = err?.response;
	if (!response) return { status: null, data: null };

	let data = response.data;
	if (typeof Blob !== "undefined" && data instanceof Blob) {
		if (data.type?.includes("json")) {
			try {
				data = JSON.parse(await data.text());
			} catch (e) {
				data = null;
			}
		} else {
			data = null;
		}
	}
	return { status: response.status, data };
}

// Rebuilt from scratch: the failed config carries a stale Content-Length.
const buildReplayConfig = (config) => ({
	method: config.method,
	url: config.url,
	data: config.__rawData,
	params: config.params,
	responseType: config.responseType,
	headers: config.headers?.["Content-Type"]
		? { "Content-Type": config.headers["Content-Type"] }
		: undefined,
	__isRetry: true,
});

authClient.interceptors.response.use(
	(response) => response,
	async (error) => {
		const config = error?.config || {};
		const { status, data } = await normalizeError(error);

		// No response at all: let the caller's own .catch() handle it.
		if (status === null) return Promise.reject(error);

		const requestPath = config.__requestPath;
		const code = data?.error_code;
		const canRetry = status === 401
			&& !config.__isRetry
			&& !isAuthFree(requestPath)
			&& (REFRESHABLE_CODES.includes(code) || (!code && isSessionEnded(data)));

		if (canRetry) {
			try {
				await doRefresh();
			} catch (e) {
				forceLogout();
				return Promise.reject(error);
			}
			return authClient(buildReplayConfig(config));
		}

		// An unrecoverable 401 ends the session; the caller's catch still runs.
		if (status === 401 && !isAuthFree(requestPath) && (code || isSessionEnded(data))) {
			forceLogout();
		}

		// Attach the parsed body so callers don't re-parse blobs themselves.
		error.__authBody = data;
		return Promise.reject(error);
	}
);
